import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import Information from 'components/Icons/Information'
import palette from 'constants/palette'
import type { RecoveryChatSource } from 'hooks/websocket/recoveryChatSource'
import { useRecoveryChat } from 'hooks/websocket/useRecoveryChat'
import { buildTranslate } from 'locales'
import React, { useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'react-router-redux'
import type { Auth } from 'reducers/auth'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import AgentTuningConfigModals from '../components/AgentTuningConfigModals'
import { RECOVERY_AI_ROOT } from '../routes'
import SimulatorEngine from './SimulatorEngine'
import SimulatorStatus from './SimulatorStatus'
import { usePendingConversationReset } from './usePendingConversationReset'
import { useSimulatorData } from './useSimulatorData'

const t = buildTranslate('recovery_ai.sandbox.standalone')
// Parent namespace for the in-tab (embedded) configure-first blocker copy,
// reused from the in-tab sandbox so the agent_tuning gate points at the on-tab
// Approved-rewards row rather than the standalone "Exit above" wording.
const tSandbox = buildTranslate('recovery_ai.sandbox')

// Gap between the decorative info icon and the app-version notice text,
// matching the LiveUpdates StatusSection note pattern.
const APP_VERSION_NOTICE_GAP = '8px'

type Props = {
  /**
   * Swappable chat source — a React hook, so it MUST be a stable reference for
   * the instance lifetime (Rules of Hooks). Key the component to switch sources,
   * never swap the prop on a mounted instance; the dev-only guard below warns if
   * it ever changes. Defaults to the ActionCable-backed `useRecoveryChat`.
   */
  useChatSource?: RecoveryChatSource
  /**
   * FE-4 / AI-173 injects the F1 staged-save commit (onSave) and the
   * dirty-prompt-on-exit (onExit) once #3684 (RecoveryConfigDraftContext /
   * ConfigModalHost) merges. Until then both default to plain navigation back to
   * the console, so FE-4 can inject the F1 behavior without re-plumbing.
   */
  onSave?: () => void
  onExit?: () => void
  /**
   * Embedded in the agent_tuning console tab (vs. a full-page standalone route).
   * When embedded the dark header drops the Exit/Save buttons — navigation is via
   * the console tabs and config-save happens in the on-tab AgentTuningConfigModals,
   * so the simulator owns nothing to "Save" and nowhere distinct to "Exit"
   * (ENG-490 / DS-2 Option A). The configure-first gate also points the merchant
   * at the on-tab Approved-rewards row instead of the standalone "Exit above" copy.
   */
  embedded?: boolean
}

const StandaloneSimulator = ({
  useChatSource = useRecoveryChat,
  onSave,
  onExit,
  embedded = false,
}: Props) => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const token = useSelector((state: { auth: Auth }) => state.auth.token || '')
  const merchantId = useSelector(
    (state: { auth: Auth }) => state.auth.current_merchant_id || 0
  )

  // The standalone shell is a free-chat "test your own" sandbox — NO guided
  // persona playback (that's wizard-only, per Figma). The guided "try your own"
  // quick-reply chips are deferred to AI-166; until then this is open-ended chat
  // with scenario === null.
  const {
    config,
    approvedRewards,
    conversationContext,
    cannedGreeting,
    isGreetingAdapting,
    isLoading,
    error,
    ready,
    retry,
  } = useSimulatorData({
    preActivation: false,
  })

  // The chat source is invoked as a hook, so it must be a stable reference for
  // the instance lifetime. Warn once in dev if it ever changes across renders
  // (the lint can't see a prop swap).
  const initialChatSourceRef = useRef(useChatSource)
  const hasWarnedChatSourceSwapRef = useRef(false)
  if (
    process.env.NODE_ENV !== 'production' &&
    !hasWarnedChatSourceSwapRef.current &&
    initialChatSourceRef.current !== useChatSource
  ) {
    hasWarnedChatSourceSwapRef.current = true
    // eslint-disable-next-line no-console
    console.error(
      '[StandaloneSimulator] `useChatSource` changed across renders. It is ' +
        'called as a hook and must be a stable reference for the component ' +
        'lifetime (Rules of Hooks). Key the component on the toggle instead of ' +
        'swapping the prop on a mounted instance.'
    )
  }

  const {
    messages,
    currentStreamingMessage,
    resolutionData,
    escalationData,
    isToolRunning,
    awaitingResponse,
    connectionError,
    sendMessage,
    reset,
  } = useChatSource({
    token,
    merchantId,
    scenario: null,
    conversationContext,
    // Gate the subscription on readiness — the chat UI (SimulatorEngine) only
    // renders when `ready`, so without this an unconfigured / loading / errored
    // merchant would open a live RecoveryChatChannel socket behind the gate
    // body. `ready` already implies !isLoading && !error.
    enabled: ready,
  })

  // TODO(FE-4/AI-173): wire Save → F1 staged-save commit and Exit → dirty-prompt
  // once #3684 merges. For now both are plain navigation back to the console
  // root via the shared RECOVERY_AI_ROOT constant (single source — no private
  // route literal).
  const handleExit = onExit ?? (() => dispatch(push(RECOVERY_AI_ROOT)))
  const handleSave = onSave ?? (() => dispatch(push(RECOVERY_AI_ROOT)))

  // AI-583 recovery: a granted reward locks the shared compose box (terminal
  // state), so every shell needs a restart. index.tsx and the wizard already
  // have one; the embedded agent_tuning tab had none — leaving the box locked
  // for good after the first grant. reset() clears resolutionData, so the offer
  // (and with it the lock) clears.
  const handleNewConversation = useCallback(() => reset(), [reset])

  // Whether a conversation is actually under way — used ONLY to compute
  // `resetDisabled` below, never to gate `onReset` itself. `ready` alone
  // (config-readiness) is NOT enough: it's true on a fresh empty sandbox,
  // where reset() is a no-op.
  const conversationStarted = ready && (messages.length > 0 || !!resolutionData)

  // `onReset` must stay UNCONDITIONAL: it gates whether SimulatorEngine mounts
  // the whole header row above the phone (showCenterHeader), so toggling it
  // mid-conversation — including on reset() itself, which clears both
  // `messages` and `resolutionData` and would otherwise flip
  // `conversationStarted` back to false the instant the merchant uses it —
  // unmounts the very link they just clicked and moves the phone. This
  // surface has no phases (unlike the wizard's guided/free split), so
  // `onReset` is pinned for the component's whole lifetime, exactly like the
  // adjacent `pinGreeting` prop below. Whether there is anything to actually
  // reset lives entirely in `resetDisabled`, alongside the in-flight guard:
  // blocked while `awaitingResponse` (sent, no reply text yet) or
  // `isToolRunning` (a tool call is executing server-side) — both leave the
  // merchant nothing visible to knowingly discard. Deliberately NOT blocked
  // on `currentStreamingMessage` being non-null: see `turnInFlight` below for
  // why that's a distinct, intentional case.
  const resetDisabled =
    awaitingResponse || isToolRunning || !conversationStarted

  // Changing agent settings reloads the conversation, so the phone always reflects the
  // new configuration from a blank slate instead of showing a transcript answered under
  // the old one. Every setting reachable from this card travels in
  // `conversation_context`, and the brain latches that context onto the chat row on the
  // FIRST message only — so a new chat is what makes a new setting take effect.
  //   context: that last step carries a backend dependency (nexus #1378), and its
  //   pre-deploy failure is a hang rather than a no-op — stated once in
  //   usePendingConversationReset's docstring rather than paraphrased at each call site.
  // A turn is in flight from send until the reply finishes — DELIBERATELY wider than
  // resetDisabled above by one term: currentStreamingMessage. useRecoveryChat clears
  // awaitingResponse on the FIRST text_chunk, so it is already false while the reply
  // streams; on that term alone a save mid-reply would arm and drain in the same
  // breath and reset() would clear currentStreamingMessage, wiping the reply the
  // merchant is reading mid-word.
  //   context: resetDisabled stays narrow on purpose. Clicking reset mid-stream is the
  //   merchant explicitly discarding a reply they can see; a save doing it silently as a
  //   side effect is not the same act.
  const turnInFlight =
    awaitingResponse || currentStreamingMessage !== null || isToolRunning
  const armConversationReset = usePendingConversationReset({
    reset,
    blocked: turnInFlight,
  })
  // The provider hands over the committed keys; this surface has no wizard state to
  // mirror into, so they are dropped rather than changing the provider's contract.
  const handleCommitted = useCallback(
    () => armConversationReset(),
    [armConversationReset]
  )

  // Early-return body keeps the gating out of a nested ternary; on the full-page
  // standalone route the dark header (with Exit/Save) always renders above this
  // so those controls are reachable even before data resolves.
  const renderBody = () => {
    if (isLoading) {
      return <SimulatorStatus variant="loading" />
    }
    if (error) {
      return <SimulatorStatus variant="error" onRetry={retry} />
    }
    if (!ready) {
      // Two gate audiences (DS-2 Option A):
      //  • embedded (agent_tuning tab): the merchant can fix this in place, so
      //    point them at the on-tab Approved-rewards row (or back to onboarding) —
      //    NOT "Exit above" (there is no Exit button here). Reuses the in-tab
      //    sandbox blocker copy so the two surfaces never drift.
      //  • standalone (full-page route): no configuration surface alongside it,
      //    so keep the standalone copy pointing at the always-present Exit control.
      // Early-return resolver (not a nested ternary) per the conventions.
      const gateMessage = () => {
        if (!embedded) return t('not_configured')
        if (!config?.onboarding_completed)
          return tSandbox('configure_first_onboarding')
        // The Approved-rewards row sits in the config card on this same tab, so
        // point the merchant at it.
        return tSandbox('configure_first_rewards')
      }
      return (
        <div className={css({ textAlign: 'center', padding: '48px 0' })}>
          <Text.BodyText3 color="grey70">{gateMessage()}</Text.BodyText3>
        </div>
      )
    }
    return (
      <>
        {/* Embedded agent_tuning intro (AI-162 layout): app-version notice above
            the phone simulator, replacing the dropped dark bar. The "ready to
            test" heading/subheading that used to sit above it was removed
            (AI-1112) — the on-tab config card and the console tab itself
            already supply that context. The AI-583 reward-lock recovery (reset
            control) moved onto SimulatorEngine's own center-column control
            (AI-1111) instead of a local heading-row button. */}
        {embedded && (
          <>
            {/* App-version requirement for the RecoveryAI Experience, above
                the phone simulator. Same subtle info-note pattern as the
                LiveUpdates StatusSection note: decorative icon (aria-hidden,
                the adjacent text conveys the meaning) + BodyText4/grey70. */}
            <div
              className={css({
                display: 'flex',
                alignItems: 'center',
                gap: APP_VERSION_NOTICE_GAP,
                marginBottom: '24px',
              })}
            >
              <span
                aria-hidden="true"
                className={css({ display: 'flex', flexShrink: 0 })}
              >
                <Information type="info" fillColor={palette.grey70} />
              </span>
              <Text.BodyText4 color="grey70">
                {t('app_version_notice')}
              </Text.BodyText4>
            </div>
          </>
        )}
        <SimulatorEngine
          messages={messages}
          currentStreamingMessage={currentStreamingMessage}
          awaitingResponse={awaitingResponse}
          isToolRunning={isToolRunning}
          onSendMessage={sendMessage}
          assistantName={config?.assistant_name}
          agentName={config?.agent_name}
          cannedGreeting={cannedGreeting}
          settingsApplying={isGreetingAdapting}
          scenario={null}
          // Render the conversation in the phone mockup (the same "what the
          // customer sees" preview the wizard uses) instead of the flat panel,
          // which read as a large empty space in the free-chat sandbox.
          chatVariant="phone"
          // AI-1111: delegate the reward-lock recovery control to the engine's
          // own small "Reset conversation" text link (already used pre-activation
          // by WizardSimulator, mirrored here) instead of the large local
          // "New conversation" Button, on both the embedded and full-page
          // layouts. `onReset` gates whether the row mounts at all, so it stays
          // UNCONDITIONAL across the whole conversation (including through
          // reset() itself) — `resetDisabled` (not folded in here) stands the
          // link down without unmounting the row/moving the phone. See
          // conversationStarted/resetDisabled above.
          onReset={handleNewConversation}
          resetDisabled={resetDisabled}
          // AI-1113: unlike the wizard's guided phase (isCannedLive), this surface
          // is ALWAYS a live, merchant-driven conversation — there is no "empty
          // log" phase to gate on. Without this the opening greeting (which
          // ChatPanel deliberately never pushes into `messages`) unmounted the
          // instant the merchant sent their first message, instead of scrolling
          // up with the rest of the transcript.
          pinGreeting
          resolutionData={resolutionData}
          escalationData={escalationData}
          connectionError={connectionError}
          config={config}
          approvedRewards={approvedRewards}
          // AI-202: forward the grantable count from the single useSimulatorData
          // source so the summary count and the reward cards (both from
          // approvedRewards) can't drift.
          rewardCount={approvedRewards.length}
          // Embedded in the agent_tuning tab: the interactive card below supplies
          // these settings via settingsCard, so suppress the duplicate read-only
          // summary here (AI-167 / DS-3 B). Only the full-page route still shows it.
          showAgentSettings={!embedded}
          // The interactive config card moves INTO the engine's right column, beside
          // the phone, matching the wizard demo layout — shown on the embedded tab in
          // both the pre-activation carve-out and the active console (identical).
          // `undefined`, never `false` — the slot falls back on nullish only.
          settingsCard={
            embedded ? (
              // Uncontrolled: the card self-manages its open row (no reasoning
              // "Tweak …" deep-link shares it anymore). Escalation rules are
              // Settings-only, so hide the Advanced disclosure here.
              //   context: `hideAdvancedRow` is now load-bearing beyond tidiness.
              //   Escalation thresholds are NOT in RecoveryConversationContextInput, so
              //   a reset cannot make the brain honour a new one — un-hiding that row
              //   would make this card's "restarts with your new settings" promise
              //   false for it.
              <AgentTuningConfigModals
                hideAdvancedRow
                // The simulator these config modals edit is THIS surface, already
                // on screen beside the card — same reasoning as the wizard's copy
                // of this card (hideSaveAndTest there): there is nowhere to route
                // "Save and test" to, so drop it and keep plain Save/Cancel.
                hideSaveAndTest
                onCommitted={handleCommitted}
                conversationRestartsOnSave
              />
            ) : undefined
          }
          // Strip the 2xN reward-preview grid: the card's "Approved Rewards" row is
          // the way in now. The inline in-chat offer image is a separate path and
          // still resolves from approvedRewards (AI-585). Unconditional (not
          // `!embedded`) on purpose — removing the grid is the design decision, not
          // an embedded-only tweak, so a revived full-page route inherits it too.
          showRewardPreviews={false}
          // Dev-only prompt accordion — off in the merchant-facing tab.
          showSandboxPrompt={false}
          // Retitle the decision log "AI reasoning" — the sole reasoning card on
          // this surface now (the separate "Agent reasoning" ReasoningPanel was
          // removed per Figma; the wizard already had none).
          decisionLogVariant="wizard"
        />
      </>
    )
  }

  return (
    <div>
      {/* The dark chrome bar (title + Exit/Save) belongs to the full-page
          standalone route. The embedded agent_tuning tab drops it entirely: the
          console tab already frames the page, and there is nothing to Exit/Save
          here (DS-2 Option A). The AI-583 reward-lock recovery (reset control) no
          longer lives in this bar (AI-1111) — it's passed as `onReset` into
          SimulatorEngine below, which renders its own small "Reset conversation"
          link above the phone on both layouts. */}
      {!embedded && (
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            backgroundColor: merchantTheme.colors.grey100,
          })}
        >
          <Text.Header4 bold color="white">
            {t('title')}
          </Text.Header4>
          <div className={css({ display: 'flex', gap: '12px' })}>
            <Button kind="secondary" onClick={handleExit}>
              {t('exit')}
            </Button>
            <Button kind="primary" onClick={handleSave}>
              {t('save_and_return')}
            </Button>
          </div>
        </div>
      )}

      <div className={css({ padding: '24px' })}>{renderBody()}</div>
    </div>
  )
}

export default StandaloneSimulator
